import { Check } from "lucide-react";
import { SERVICES } from "#/lib/constants";
import { img } from "#/lib/images";
import { CTAButton } from "#/components/shared/CTAButton";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { useScrollAnimation } from "#/hooks/useScrollAnimation";

const COUNT = SERVICES.length;

type Service = (typeof SERVICES)[number];

/**
 * One in-depth "Standard block" on /services: photo, expanded overview,
 * "How we do it best" checklist, estimate CTA. Photo alternates sides by index.
 * `id` = service slug so the hero and the home teaser can deep-link here.
 */
export function ServiceDetailSection({ service, index }: { service: Service; index: number }) {
  const { ref, isVisible } = useScrollAnimation();
  const flipped = index % 2 === 1;

  return (
    <section
      id={service.slug}
      className={`scroll-mt-20 py-16 lg:py-24 ${flipped ? "bg-light-bg" : "bg-white"}`}
    >
      <div
        ref={ref}
        className={`mx-auto grid max-w-7xl items-center gap-10 px-4 transition-all duration-700 motion-reduce:transition-none sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        <img
          src={img(service.detailImg, { w: 1000, h: 750 })}
          alt={service.title}
          width={1000}
          height={750}
          loading="lazy"
          decoding="async"
          className={`aspect-[4/3] w-full rounded-2xl object-cover shadow-xl ${
            flipped ? "lg:order-2" : ""
          }`}
        />

        <div>
          <SectionLabel>
            {String(index + 1).padStart(2, "0")} / {String(COUNT).padStart(2, "0")}
          </SectionLabel>
          <h2 className="mt-3 font-heading text-3xl font-black uppercase tracking-tight text-graphite sm:text-4xl">
            {service.title}
          </h2>
          {service.overview.map((paragraph) => (
            <p key={paragraph.slice(0, 32)} className="mt-4 text-[15px] leading-relaxed text-graphite/70">
              {paragraph}
            </p>
          ))}

          <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-graphite">
            How we do it best
          </p>
          <ul className="mt-3 space-y-2.5">
            {service.craft.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-graphite/80">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                {item}
              </li>
            ))}
          </ul>

          <CTAButton href="/quote" variant="flame" className="mt-8 uppercase tracking-wide">
            Get a free estimate
          </CTAButton>
        </div>
      </div>
    </section>
  );
}
